import { Globe, Check, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { cn } from "@/modern/utils";
import { useBoardContextContext } from "../context/BoardContextContext";

const PAGE_PICKER_SKELETON_ROWS = 6;

export const PagePickerStep = () => {
  const {
    crawledPages,
    selectedUrls,
    setSelectedUrls,
    handleAnalysePages,
    isSubmittingPagePick,
    isLoadingMapUrl,
    boardContextRecord,
    handleRegenerate,
    canContinueToQuestions,
    handleContinueToQuestions,
  } = useBoardContextContext();

  const toggleUrl = (url: string) => {
    if (selectedUrls.includes(url)) {
      setSelectedUrls(selectedUrls.filter((u) => u !== url));
    } else {
      setSelectedUrls([...selectedUrls, url]);
    }
  };

  const selectAll = () => {
    setSelectedUrls(crawledPages.map((p) => p.url));
  };

  const clearAll = () => setSelectedUrls([]);

  const hostname = boardContextRecord?.websiteUrl
    ? (() => {
        try { return new URL(boardContextRecord.websiteUrl).hostname; } catch { return boardContextRecord.websiteUrl; }
      })()
    : "";

  const selectionLabel =
    crawledPages.length > 0
      ? `${selectedUrls.length} of ${crawledPages.length} selected`
      : `${selectedUrls.length} selected`;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">Pick the pages to analyse</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isLoadingMapUrl ? (
                <>
                  Refreshing pages from{" "}
                  <span className="font-medium text-foreground">{hostname || "your site"}</span>…
                </>
              ) : (
                <>
                  We mapped{" "}
                  <span className="font-medium text-foreground">{hostname}</span> and ranked these pages
                  against your project's gaps. Select the pages you want to include in the analysis.
                </>
              )}
            </p>
          </div>
          {isLoadingMapUrl ? (
            <Skeleton className="h-6 w-24 shrink-0 rounded-full" />
          ) : (
            <span className="shrink-0 text-xs font-medium text-muted-foreground bg-muted rounded-full px-2.5 py-1">
              {selectionLabel}
            </span>
          )}
        </div>

        {/* Bulk actions */}
        <div className="flex gap-2 mt-3">
          {isLoadingMapUrl ? (
            <>
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-10" />
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={selectAll}
                disabled={crawledPages.length === 0 || selectedUrls.length === crawledPages.length}
                className="text-xs text-primary hover:underline disabled:opacity-40 disabled:no-underline"
              >
                Select all
              </button>
              <span className="text-xs text-muted-foreground">·</span>
              <button
                type="button"
                onClick={clearAll}
                disabled={selectedUrls.length === 0}
                className="text-xs text-muted-foreground hover:text-foreground hover:underline disabled:opacity-40 disabled:no-underline"
              >
                Clear
              </button>
            </>
          )}
        </div>
      </div>

      {/* Page list */}
      <div className="flex-1 overflow-y-auto px-6 py-3 space-y-2">
        {isLoadingMapUrl ? (
          Array.from({ length: PAGE_PICKER_SKELETON_ROWS }).map((_, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3.5 rounded-lg border border-border bg-card"
            >
              <Skeleton className="h-4 w-4 shrink-0 rounded" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4 max-w-xs" />
                <Skeleton className="h-3 w-full max-w-md" />
                <Skeleton className="h-3 w-5/6 max-w-sm" />
              </div>
            </div>
          ))
        ) : crawledPages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Globe className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No pages discovered.</p>
            <button
              type="button"
              onClick={handleRegenerate}
              className="text-xs text-primary hover:underline mt-2"
            >
              Go back and try a different URL
            </button>
          </div>
        ) : (
          crawledPages.map((page) => {
            const isSelected = selectedUrls.includes(page.url);
            return (
              <button
                key={page.url}
                type="button"
                onClick={() => toggleUrl(page.url)}
                className={cn(
                  "w-full text-left flex items-start gap-3 p-3.5 rounded-lg border transition-all duration-150 cursor-pointer",
                  isSelected
                    ? "border-primary/40 bg-primary/5"
                    : "border-border bg-card hover:border-primary/30 hover:bg-muted/30"
                )}
              >
                {/* Checkbox */}
                <div
                  className={cn(
                    "mt-0.5 shrink-0 flex h-4 w-4 items-center justify-center rounded border transition-colors",
                    isSelected ? "bg-primary border-primary" : "border-border"
                  )}
                >
                  {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                </div>

                {/* Page info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground line-clamp-1">
                      {page.title || page.url}
                    </span>
                    {page.url === boardContextRecord?.websiteUrl && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                        your page
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{page.url}</p>
                  {page.description && (
                    <p className="text-xs text-muted-foreground/80 mt-1 line-clamp-2">
                      {page.description}
                    </p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Footer actions */}
      <div className="shrink-0 px-6 py-4 border-t border-border flex items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRegenerate}
          disabled={isLoadingMapUrl}
          className="gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Button>

        <Button
          onClick={canContinueToQuestions ? handleContinueToQuestions : handleAnalysePages}
          disabled={isLoadingMapUrl || selectedUrls.length === 0 || isSubmittingPagePick}
          className="gap-2"
        >
          {isSubmittingPagePick ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Starting…
            </>
          ) : canContinueToQuestions ? (
            "Next"
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Analyse {selectedUrls.length > 0 ? `${selectedUrls.length} page${selectedUrls.length > 1 ? "s" : ""}` : "pages"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
